import { useCallback, useRef, useState } from "react";
import { requestChatCompletionStream } from "utils/aiClient.ts";

interface StreamHandlers {
  onToken?: (token: string, accumulated: string) => void;
  onDone?: () => void;
  onError?: (err: unknown) => void;
}

export function useAIReasoningStream() {
  const [streaming, setStreaming] = useState<boolean>(false);
  const [reasoningText, setReasoningText] = useState<string>("");
  const latestReasoningRef = useRef<string>("");
  const abortRef = useRef<AbortController | null>(null);

  const start = useCallback(async (prompt: string, handlers: StreamHandlers = {}) => {
    // Guard against duplicate starts (e.g., React StrictMode)
    if (abortRef.current) return;
    setStreaming(true);
    setReasoningText("");
    latestReasoningRef.current = "";
    const controller = new AbortController();
    abortRef.current = controller;
    try {
      await requestChatCompletionStream(prompt, {
        signal: controller.signal,
        onToken: (t, all) => {
          if (handlers.onToken) handlers.onToken(t, all);
        },
        onReasoning: (_t, all) => {
          latestReasoningRef.current = all;
          setReasoningText(all);
        },
        onDone: () => {
          setStreaming(false);
          abortRef.current = null;
          if (handlers.onDone) handlers.onDone();
        },
        onError: (e) => {
          setStreaming(false);
          abortRef.current = null;
          if (handlers.onError) handlers.onError(e);
        },
      });
    } finally {
      // Ensure abort ref cleared if control flow skipped callbacks
      if (abortRef.current === controller) abortRef.current = null;
    }
  }, []);

  const cancel = useCallback(() => {
    if (abortRef.current) abortRef.current.abort();
  }, []);

  return { streaming, reasoningText, latestReasoningRef, start, cancel };
}


